import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CheckIcon, XIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { EntitySheet } from "../../entity-sheet";
import ScoreBadge from "../../score-badge";
import { useNotificationsProvider } from "../../../context/NotificationsContext";
import { useInsightFeedbackMutation } from "../../../services/api/insightsApi";
import type { Insight } from "../../../entities/insights";

/*
 * Insight detail drawer.
 *
 * All text renders as plain React children — never markdown, never
 * dangerouslySetInnerHTML. The strings are LLM output derived from
 * user-uploaded media, so escaping is the only thing standing between an
 * uploaded image caption and stored XSS.
 */

interface InsightSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  insight?: Insight;
  loading?: boolean;
  canGiveFeedback?: boolean;
}

export default function InsightSheet({
  open,
  onOpenChange,
  insight,
  loading = false,
  canGiveFeedback = true,
}: InsightSheetProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showNotification } = useNotificationsProvider();
  const [sendFeedback, { isLoading: isSending }] = useInsightFeedbackMutation();
  const [note, setNote] = useState("");

  async function handleFeedback(status: "validated" | "dismissed") {
    if (!insight) return;
    try {
      await sendFeedback({ fingerprint: insight.fingerprint, status, note }).unwrap();
      showNotification("success", t(`insights.feedback.${status}-ok`, "Thanks — noted."));
      setNote("");
      onOpenChange(false);
    } catch {
      showNotification("error", t("insights.feedback.error", "Could not save your feedback."));
    }
  }

  const alreadyRated = insight?.status && insight.status !== "open";

  return (
    <EntitySheet
      open={open}
      onOpenChange={onOpenChange}
      canEdit={false}
      size="lg"
      title={
        loading || !insight ? (
          <Skeleton className="h-6 w-64" />
        ) : (
          <span className="flex items-center gap-2">
            <ScoreBadge score={insight.score} />
            <span>{insight.title}</span>
          </span>
        )
      }
      footer={
        canGiveFeedback && insight && !alreadyRated ? (
          <div className="flex w-full flex-col gap-2">
            <Textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder={t(
                "insights.feedback.note-placeholder",
                "Optional: why? This is fed back into future analysis.",
              )}
              rows={2}
            />
            <div className="flex gap-2">
              <Button
                className="flex-1"
                disabled={isSending}
                onClick={() => handleFeedback("validated")}
              >
                <CheckIcon /> {t("insights.feedback.validate", "Confirm")}
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                disabled={isSending}
                onClick={() => handleFeedback("dismissed")}
              >
                <XIcon /> {t("insights.feedback.dismiss", "Dismiss")}
              </Button>
            </div>
          </div>
        ) : null
      }
    >
      {loading || !insight ? (
        <div className="space-y-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      ) : (
        <div className="space-y-6">
          <section>
            <p className="text-sm leading-relaxed">{insight.description}</p>
          </section>

          {insight.recommendations?.length ? (
            <section className="space-y-2">
              <h3 className="text-sm font-medium">
                {t("insights.detail.recommendations", "Recommended actions")}
              </h3>
              <ul className="list-disc space-y-1 pl-5 text-sm">
                {insight.recommendations.map((recommendation, index) => (
                  <li key={index}>{recommendation}</li>
                ))}
              </ul>
            </section>
          ) : null}

          {insight.links?.length ? (
            <section className="space-y-2">
              <h3 className="text-sm font-medium">
                {t("insights.detail.evidence", "Evidence")}
              </h3>
              <ul className="space-y-1 text-sm">
                {insight.links.map((link) => (
                  <li key={`${link.inspectionID}-${link.resourceID ?? ""}`}>
                    <Button
                      variant="link"
                      className="h-auto p-0"
                      onClick={() =>
                        navigate(`/dashboard/inspections?detail=${link.inspectionID}`)
                      }
                    >
                      {link.inspectionNumber ??
                        t("insights.detail.inspection", {
                          defaultValue: "Inspection {{id}}",
                          id: link.inspectionID,
                        })}
                    </Button>
                    {link.resourceName ? (
                      <span className="text-muted-foreground"> — {link.resourceName}</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {insight.snippets?.length ? (
            <section className="space-y-2">
              <h3 className="text-sm font-medium">
                {t("insights.detail.snippets", "What was detected")}
              </h3>
              <ul className="space-y-2">
                {insight.snippets.map((snippet, index) => (
                  <li
                    key={index}
                    className="rounded-md bg-muted p-2 text-sm text-muted-foreground"
                  >
                    {snippet}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {alreadyRated ? (
            <>
              <Separator />
              <p className="text-sm text-muted-foreground">
                {t(`insights.feedback.already-${insight.status}`, "Already reviewed.")}
                {insight.feedback?.note ? ` — ${insight.feedback.note}` : null}
              </p>
            </>
          ) : null}
        </div>
      )}
    </EntitySheet>
  );
}
